import { useEffect, createContext, useContext } from "react";
import { roleName } from "./admin/shared/admin-utils";
import { ContractViewModel, Role } from "./contract/model/ContractViewModel";
import {
  TrackingContext as TeamschemeTrackingContext,
  TrackEventProps,
  TrackingValue,
} from "@teamscheme/tracking";

declare global {
  interface Window {
    dataLayer: Array<any>;
  }
}

export function useTrackContext(
  attribute: string,
  value: TrackingValue | null | undefined
) {
  const tracking = useTracking();
  useEffect(() => {
    if (value !== null && value !== undefined) {
      tracking.context({ [attribute]: value });
    }
  }, [attribute, tracking, value]);
}

export interface IAppTrackingContext {
  event(props: TrackEventProps): void;
  pageView({ title, path }: { title: string; path: string }): void;
  contractLoad(contract: ContractViewModel, role: Role): void;
  experiment(experimentName: string): void;
  appLoad(): void;
  context(contextObject: { [attribute: string]: TrackingValue }): void;
}

const AppTrackingContext = createContext<IAppTrackingContext | null>(null);

export function AppTrackingProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  function trackEvent({
    category,
    action,
    label,
    value,
    additionalProps,
  }: TrackEventProps) {
    window.dataLayer.push({
      event: "event",
      category,
      action,
      label,
      value,
      additionalProps,
    });

    console.log("event", {
      category,
      action,
      label,
      value,
      additionalProps,
    });
  }

  function addTrackingContext(contextObject: {
    [attribute: string]: TrackingValue;
  }) {
    for (const [attribute, value] of Object.entries(contextObject)) {
      window.dataLayer.push({ [attribute]: value });
    }
  }

  function trackContractLoad(contract: ContractViewModel, role: Role) {
    const params = {
      id: contract.id,
      role: roleName(role),
    };

    window.dataLayer.push({
      event: "contract",
      ...params,
    });

    console.log("contract", params);
  }

  function trackExperiment(experimentName: string) {
    window.dataLayer.push({
      event: "experiment",
      experimentName,
    });

    console.log("experiment", experimentName);
  }

  function trackAppLoad() {
    window.dataLayer.push({
      event: "load",
      appVersion: process.env.REACT_APP_VERSION,
    });
  }

  function trackPageView({ title, path }: { title: string; path: string }) {
    window.dataLayer.push({
      event: "VirtualPageView",
      virtualPageURL: path,
      virtualPageTitle: title,
    });

    console.log("pageview", path, title);
  }

  const trackingContext = { trackEvent };
  const appTrackingContext = {
    event: trackEvent,
    pageView: trackPageView,
    appLoad: trackAppLoad,
    contractLoad: trackContractLoad,
    experiment: trackExperiment,
    context: addTrackingContext,
  };

  return (
    <TeamschemeTrackingContext.Provider value={trackingContext}>
      <AppTrackingContext.Provider value={appTrackingContext}>
        {children}
      </AppTrackingContext.Provider>
    </TeamschemeTrackingContext.Provider>
  );
}

export function useTracking() {
  const context = useContext(AppTrackingContext);
  if (!context) {
    throw new Error("No AppTrackingContext found!");
  }
  return context;
}

export function externalLinkEvent(href: string) {
  return {
    ...EVENTS.COMMON.OPEN_EXTERNAL_LINK,
    label: href,
  };
}

const CATEGORIES = {
  COMMON: "Common",
  INVITATION: "Invitation",
  SIGNING: "Signing",
  FOOTER: "Footer",
  LANDING_PAGE: "Landing Page",
  TRIAL_MEMBERSHIP: "Trial Membership",
  CAR_INSURANCE: "Car Insurance",
  CAR_LOAN: "Car Loan",
  CONFIRMATION_PAGE: "Confirmation",
  CAR_LOAN_CONFIRMATION_PAGE: "Confirmation/Car Loan",
  CONFIRMATION_PAGE_TRIAL_MEMBERSHIP: "Confirmation/Trial Membership",
  PREVIEW: "Preview",
  FAQ_PAGE: "FAQ Page",
  STATIC_CONTRACTS: "Static Contracts",
  SAVE_CONTRACTS_MODAL: "Save Contracts Modal",
  PAYMENT: "Payment",
  ATTACHMENTS_PAGE: "Attachments Page",
  CONTRACT_UPDATES_MODAL: "Contract Updates Modal",
  CORONA_BANNER: "Corona Banner",
  CONTRACT_TYPES: "Contract Types",
  HEADER: "Header",
};

export const EVENTS = {
  CONTRACT_TYPES: {
    SELECT_OPTION: {
      category: CATEGORIES.CONTRACT_TYPES,
      action: "Select Option",
    },
    CONTINUE: {
      category: CATEGORIES.CONTRACT_TYPES,
      action: "Continue",
    },
    READ_MORE: {
      category: CATEGORIES.CONTRACT_TYPES,
      action: "Read More",
    },
    OPEN_LINK_SERVICE: {
      category: CATEGORIES.CONTRACT_TYPES,
      action: "Open Link Service",
    },
    LOG_IN: {
      category: CATEGORIES.CONTRACT_TYPES,
      action: "Log In",
    },
    ACTIVATE_TRIAL_MEMBERSHIP: {
      category: CATEGORIES.CONTRACT_TYPES,
      action: "Activate Trial Membership",
    },
    UPDATE_CONSENTS: {
      category: CATEGORIES.CONTRACT_TYPES,
      action: "Update Consents",
    },
  },
  CORONA_BANNER: {
    CLOSE: {
      category: CATEGORIES.CORONA_BANNER,
      action: "Close",
    },
  },
  COMMON: {
    OPEN_EXTERNAL_LINK: {
      category: CATEGORIES.COMMON,
      action: "Open External Link",
    },
    LOG_OUT: {
      category: CATEGORIES.COMMON,
      action: "Log Out",
    },
    LOG_IN: {
      category: CATEGORIES.COMMON,
      action: "Log In",
    },
  },
  INVITATION: {
    SEND: {
      category: CATEGORIES.INVITATION,
      action: "Send",
    },
    CHANGE_RECIPIENT: {
      category: CATEGORIES.INVITATION,
      action: "Change Recipient",
    },
    INVITE_BY_EMAIL: {
      category: CATEGORIES.INVITATION,
      action: "Invite by Email",
    },
    INVITE_BY_SMS: {
      category: CATEGORIES.INVITATION,
      action: "Invite by SMS",
    },
  },
  SIGNING: {
    CANCEL: {
      category: CATEGORIES.SIGNING,
      action: "Cancel",
    },
    SELECT_PAYMENT_METHOD: {
      category: CATEGORIES.SIGNING,
      action: "Select Payment Method",
    },
    SELECT_SIGNING_METHOD: {
      category: CATEGORIES.SIGNING,
      action: "Select Signing Method",
    },
    GO_TO_BANK_ID_SIGNING: {
      category: CATEGORIES.SIGNING,
      action: "Go To BankID Signing",
    },
    SELECT_OTHER_SIGNING_METHOD: {
      category: CATEGORIES.SIGNING,
      action: "Select Other Signign Method",
    },
    CANCEL_BANK_ID: {
      category: CATEGORIES.SIGNING,
      action: "Cancel BankID",
    },
  },
  CONFIRMATION_PAGE: {
    DOWNLOAD_PDF: {
      category: CATEGORIES.CONFIRMATION_PAGE,
      action: "Download PDF",
    },
    OPEN_EXTERNAL_LINK: {
      category: CATEGORIES.CONFIRMATION_PAGE,
      action: "Open External Link",
    },
    ADD_CUSTOMER_REFERENCE: {
      category: CATEGORIES.CONFIRMATION_PAGE,
      action: "Add Customer Reference",
    },
    ADD_CUSTOMER_REFERENCE_ERROR: {
      category: CATEGORIES.CONFIRMATION_PAGE,
      action: "Add Customer Reference Error",
    },
  },
  CONFIRMATION_PAGE_TRIAL_MEMBERSHIP: {
    ACTIVATE_TRIAL_MEMBERSHIP: {
      category: CATEGORIES.CONFIRMATION_PAGE_TRIAL_MEMBERSHIP,
      action: "Activate",
    },
  },
  FOOTER: {
    OPEN_OLD_CONTRACT: {
      category: CATEGORIES.FOOTER,
      action: "Open Old Contract",
    },
    EMAIL_CUSTOMER_SERVICE: {
      category: CATEGORIES.FOOTER,
      action: "Email Customer Service",
    },
  },
  LANDING_PAGE: {
    OPEN_OLD_CONTRACT: {
      category: CATEGORIES.LANDING_PAGE,
      action: "Open Old Contract",
    },
    CREATE_CONTRACT: {
      category: CATEGORIES.LANDING_PAGE,
      action: "Create Contract",
    },
    OPEN_SAVED_CONTRACT: {
      category: CATEGORIES.LANDING_PAGE,
      action: "Open Saved Contract",
    },
    START_NEW: {
      category: CATEGORIES.LANDING_PAGE,
      action: "Start New",
    },
    OPEN_PREVIEW: {
      category: CATEGORIES.LANDING_PAGE,
      action: "Open Preview",
    },
    OPEN_QUESTION: {
      category: CATEGORIES.LANDING_PAGE,
      action: "Open Question",
    },
  },
  TRIAL_MEMBERSHIP: {
    CHOOSE: {
      category: CATEGORIES.TRIAL_MEMBERSHIP,
      action: "Choose",
    },
    NEXT: {
      category: CATEGORIES.TRIAL_MEMBERSHIP,
      action: "Next",
    },
  },
  CAR_INSURANCE: {
    CONFIRM: {
      category: CATEGORIES.CAR_INSURANCE,
      action: "Confirm",
    },
    NEXT: {
      category: CATEGORIES.CAR_INSURANCE,
      action: "Next",
    },
    REMOVE: {
      category: CATEGORIES.CAR_INSURANCE,
      action: "Remove",
    },
    DECLINE: {
      category: CATEGORIES.CAR_INSURANCE,
      action: "Decline",
    },
    CALCULATE: {
      category: CATEGORIES.CAR_INSURANCE,
      action: "Calculate",
    },
    CHECK_PRICE: {
      category: CATEGORIES.CAR_INSURANCE,
      action: "Check Price",
    },
  },
  CAR_LOAN: {
    SHOW_CALCULATOR: {
      category: CATEGORIES.CAR_LOAN,
      action: "Show calculator",
    },
    HIDE_CALCULATOR: {
      category: CATEGORIES.CAR_LOAN,
      action: "Hide calculator",
    },
    APPLY: {
      category: CATEGORIES.CAR_LOAN,
      action: "Apply",
    },
    CALCULATE: {
      category: CATEGORIES.CAR_LOAN,
      action: "Calculate",
    },
  },
  CAR_LOAN_CONFIRMATION_PAGE: {
    APPLY: {
      category: CATEGORIES.CAR_LOAN_CONFIRMATION_PAGE,
      action: "Apply",
    },
    CALCULATE: {
      category: CATEGORIES.CAR_LOAN_CONFIRMATION_PAGE,
      action: "Calculate",
    },
  },
  PREVIEW: {
    FILTER_FIELD_DESCRIPTIONS: {
      category: CATEGORIES.PREVIEW,
      action: "Filter field descriptions",
    },
    DOWNLOAD_PDF: {
      category: CATEGORIES.PREVIEW,
      action: "Download PDF",
    },
  },
  FAQ: {
    FILTER: {
      category: CATEGORIES.FAQ_PAGE,
      action: "Filter",
    },
    OPEN_QUESTION: {
      category: CATEGORIES.FAQ_PAGE,
      action: "Open Question",
    },
  },
  STATIC_CONTRACTS: {
    DOWNLOAD: {
      category: CATEGORIES.STATIC_CONTRACTS,
      action: "Download",
    },
  },
  SAVE_CONTRACT_MODAL: {
    OPEN: {
      category: CATEGORIES.SAVE_CONTRACTS_MODAL,
      action: "Open Modal",
    },
    SEND_LINK: {
      category: CATEGORIES.SAVE_CONTRACTS_MODAL,
      action: "Send Link",
    },
    COPY_LINK: {
      category: CATEGORIES.SAVE_CONTRACTS_MODAL,
      action: "Copy Link",
    },
    REMOVE_SAVED: {
      category: CATEGORIES.SAVE_CONTRACTS_MODAL,
      action: "Remove Contract",
    },
    OPEN_SAVED_CONTRACT: {
      category: CATEGORIES.SAVE_CONTRACTS_MODAL,
      action: "Open Saved Contract",
    },
  },
  PAYMENT: {
    INITIATE_VIPPS_PAYMENT: {
      category: CATEGORIES.PAYMENT,
      action: "Initiate Vipps Payment",
    },
    INITIATE_CHECKOUT: {
      category: CATEGORIES.PAYMENT,
      action: "Initiate Checkout",
    },
    CREATE_CARD_PAYMENT: {
      category: CATEGORIES.PAYMENT,
      action: "Create Card Payment",
    },
    CARD_PAYMENT_DECLINED: {
      category: CATEGORIES.PAYMENT,
      action: "Card Payment Declined",
    },
    CARD_PAYMENT_COMPLETED: {
      category: CATEGORIES.PAYMENT,
      action: "Card Payment Completed",
    },
  },
  ATTACHMENTS: {
    REMOVE: {
      category: CATEGORIES.ATTACHMENTS_PAGE,
      action: "Remove",
    },
    DROP: {
      category: CATEGORIES.ATTACHMENTS_PAGE,
      action: "Drag-and-Drop to Upload",
    },
    UPLOAD: {
      category: CATEGORIES.ATTACHMENTS_PAGE,
      action: "Click to Upload",
    },
    DOWNLOAD_ZIP: {
      category: CATEGORIES.ATTACHMENTS_PAGE,
      action: "Download Zip",
    },
  },
  CONTRACT_UPDATES_MODAL: {
    OPEN: {
      category: CATEGORIES.CONTRACT_UPDATES_MODAL,
      action: "Open",
    },
    OK: {
      category: CATEGORIES.CONTRACT_UPDATES_MODAL,
      action: "Ok",
    },
    DISMISS: {
      category: CATEGORIES.CONTRACT_UPDATES_MODAL,
      action: "Dismiss",
    },
  },
  HEADER: {
    BECOME_MEMBER: {
      category: CATEGORIES.HEADER,
      action: "Become Member",
    },
    LOG_IN: {
      category: CATEGORIES.HEADER,
      action: "Log In",
    },
    LOG_OUT: {
      category: CATEGORIES.HEADER,
      action: "Log Out",
    },
    PREVIEW: {
      category: CATEGORIES.HEADER,
      action: "Open Preview",
    },
    FAQ: {
      category: CATEGORIES.HEADER,
      action: "Open FAQ",
    },
  },
};
